import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import * as Yup from "yup";

import { createCustomer } from "src/api/customer";
import { TextField } from "src/components/form";
import { Button } from "src/components/ui/button";
import { usePortalContext } from "src/context/PortalContext";
import { useAuth } from "src/providers/AuthProvider";
import { getCustomizationValue, showSuccessToast } from "src/lib/utils";
import type { CustomerRow } from "src/types/customer";

const validationSchema = Yup.object().shape({
	first_name: Yup.string().required("This field is required"),
	last_name: Yup.string().required("This field is required"),
	phone: Yup.string().required("This field is required"),
	email: Yup.string().email("Enter a valid email").required("This field is required"),
	address: Yup.string(),
	business_name: Yup.string(),
	business_address: Yup.string(),
});

interface CustomerFormValues {
	first_name: string;
	last_name: string;
	phone: string;
	email: string;
	address: string;
	business_name: string;
	business_address: string;
}

const initialValues: CustomerFormValues = {
	first_name: "",
	last_name: "",
	phone: "",
	email: "",
	address: "",
	business_name: "",
	business_address: "",
};

interface NewCustomerModalProps {
	/** Fired with the newly created customer — lets a caller select it inline (e.g. the invoice form). */
	onCreate?: (customer?: CustomerRow) => void;
}

const NewCustomerModal = ({ onCreate }: NewCustomerModalProps) => {
	const { user } = useAuth();
	const queryClient = useQueryClient();
	const closePortal = usePortalContext();

	const customerLabel = getCustomizationValue(user, "customer_label", true) || "Customer";

	const { mutate, isLoading } = useMutation(createCustomer, {
		onSuccess: ({ message, data }) => {
			showSuccessToast(message ?? "");
			onCreate?.(data);
			queryClient.invalidateQueries(["customers"]);
			closePortal();
		},
	});

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(values) => mutate(values)}>
			{({ values, handleChange, handleSubmit, errors, touched }) => (
				<form onSubmit={handleSubmit} className="tw-grid tw-gap-4">
					<div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
						<TextField
							label="First name"
							name="first_name"
							placeholder="First name"
							value={values.first_name}
							error={touched.first_name && errors.first_name}
							onChange={handleChange}
							required
							autoFocus
						/>
						<TextField
							label="Last name"
							name="last_name"
							placeholder="Last name"
							value={values.last_name}
							error={touched.last_name && errors.last_name}
							onChange={handleChange}
							required
						/>
						<TextField
							label="Email"
							name="email"
							type="email"
							placeholder="name@example.com"
							value={values.email}
							error={touched.email && errors.email}
							onChange={handleChange}
							required
						/>
						<TextField
							label="Phone"
							name="phone"
							placeholder="Phone number"
							value={values.phone}
							error={touched.phone && errors.phone}
							onChange={handleChange}
							required
						/>
					</div>

					<TextField
						label="Address"
						name="address"
						placeholder="Address"
						value={values.address}
						error={touched.address && errors.address}
						onChange={handleChange}
					/>

					<div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
						<TextField
							label="Business name"
							name="business_name"
							placeholder="Business name"
							value={values.business_name}
							error={touched.business_name && errors.business_name}
							onChange={handleChange}
						/>
						<TextField
							label="Business address"
							name="business_address"
							placeholder="Business address"
							value={values.business_address}
							error={touched.business_address && errors.business_address}
							onChange={handleChange}
						/>
					</div>

					<Button type="submit" variant="secondary" className="tw-mt-1 tw-w-full" disabled={isLoading}>
						{isLoading ? "Creating..." : `Create ${customerLabel}`}
					</Button>
				</form>
			)}
		</Formik>
	);
};

export default NewCustomerModal;
